import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { budgetsApi, departmentsApi, expensesApi } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const ExpenseCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [vendor_id, setVendorId] = useState<number | ''>(''); // optional, currently not exposed in UI
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [department_id, setDepartmentId] = useState<number | ''>('');
  const [invoice_no, setInvoiceNo] = useState('');
  const [sub_total_amount, setSubTotalAmount] = useState<string>('');
  const [tax_amount, setTaxAmount] = useState<string>('');
  const [inv_date, setInvDate] = useState('');

  const numericSubTotal = parseFloat(sub_total_amount) || 0;
  const numericTax = tax_amount === '' ? 0 : parseFloat(tax_amount) || 0;
  const computedTotal = numericSubTotal + numericTax;

  const invoiceYear = inv_date ? new Date(inv_date).getFullYear() : undefined;

  const { data: departmentsRes } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
  });

  const departments = useMemo(() => {
    try {
      const body = (departmentsRes as any)?.data;
      const inner = body?.data;
      if (Array.isArray(inner)) return inner;
      if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
    } catch {
      // ignore
    }
    return [];
  }, [departmentsRes]);

  const { data: budgetRes } = useQuery({
    queryKey: ['budgets-for-expense', invoiceYear, department_id],
    enabled: !!invoiceYear,
    queryFn: () =>
      budgetsApi.list({
        year: invoiceYear,
        departmentId: typeof department_id === 'number' ? department_id : undefined,
        isActive: 'Yes',
        perPage: 1,
      }),
  });

  const budgetList = useMemo(() => {
    try {
      const body = (budgetRes as any)?.data;
      const inner = body?.data;
      if (Array.isArray(inner)) return inner;
      if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
    } catch {
      // ignore
    }
    return [];
  }, [budgetRes]);

  const activeBudget = budgetList[0] as any | undefined;
  const remainingBefore = activeBudget ? Number(activeBudget.remaining_budget || 0) : 0;
  const remainingAfter = remainingBefore - computedTotal;
  const ceiling = activeBudget ? Number(activeBudget.ceiling_amount || 0) : 0;
  const spent = activeBudget ? Number(activeBudget.spent_amount || 0) : 0;
  const pctAfter = ceiling > 0 ? ((spent + computedTotal) / ceiling) * 100 : 0;
  const budgetStatus =
    !activeBudget
      ? null
      : pctAfter > 100
      ? 'danger'
      : pctAfter >= 80
      ? 'warning'
      : 'ok';

  const create = useMutation({
    mutationFn: () =>
      expensesApi.create({
        vendor_id: typeof vendor_id === 'number' ? vendor_id : undefined,
        name,
        description: description || null,
        department_id: typeof department_id === 'number' ? department_id : null,
        invoice_no,
        sub_total_amount: numericSubTotal,
        tax_amount: tax_amount === '' ? null : numericTax,
        inv_date,
      }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`/dashboard/expenses/${id}`);
      else navigate('/dashboard/expenses');
      toast.success('Expense created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const canSubmit =
    name.trim().length > 0 &&
    invoice_no.trim().length > 0 &&
    inv_date.trim().length > 0;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">New Expense</h1>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Link each expense to a department or Misc and see remaining budget in real time.</p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Vendor ID removed from UI; reserved for future integrations */}
        <FormField label="Department">
          <select
            className={inputClass}
            value={department_id === '' ? '' : department_id}
            onChange={(e) => setDepartmentId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Misc (no department)</option>
            {departments.map((d: any) => (
              <option key={d.id} value={d.id}>
                {d.department_name}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Invoice date">
          <input className={inputClass} type="date" value={inv_date} onChange={(e) => setInvDate(e.target.value)} required />
        </FormField>

        <FormField label="Expense name">
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
        </FormField>
        <FormField label="Invoice no.">
          <input className={inputClass} value={invoice_no} onChange={(e) => setInvoiceNo(e.target.value)} required />
        </FormField>

        <FormField label="Sub total amount (MWK)">
          <input className={inputClass} type="number" step="0.01" min={0} value={sub_total_amount} onChange={(e) => setSubTotalAmount(e.target.value)} />
        </FormField>
        <FormField label="Tax amount (MWK)">
          <input className={inputClass} type="number" step="0.01" min={0} value={tax_amount} onChange={(e) => setTaxAmount(e.target.value)} />
        </FormField>
        <FormField label="Total amount (computed)">
          <input
            className={inputClass}
            type="number"
            step="0.01"
            min={0}
            value={computedTotal.toFixed(2)}
            readOnly
          />
        </FormField>

        <div className="md:col-span-3">
          <FormField label="Description (optional)">
            <textarea
              className={inputClass}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>
        </div>

        <div className="md:col-span-3 flex flex-col gap-2 mt-2">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-slate-900/40 px-4 py-3 text-xs text-slate-700 dark:text-slate-200">
            {invoiceYear && activeBudget ? (
              <>
                <div className="font-semibold mb-1">
                  Budget {invoiceYear} – {activeBudget.department?.department_name || 'All Departments'}
                </div>
                <div className="flex flex-wrap gap-4">
                  <div>Ceiling: {ceiling.toLocaleString()}</div>
                  <div>Remaining before: {remainingBefore.toLocaleString()}</div>
                  <div>Remaining after: {remainingAfter.toLocaleString()}</div>
                  <div>
                    Utilization after: {pctAfter.toFixed(1)}%
                    {budgetStatus === 'warning' && ' (approaching limit)'}
                    {budgetStatus === 'danger' && ' (over budget)'}
                  </div>
                </div>
              </>
            ) : invoiceYear ? (
              <div>No budget set for this year/department.</div>
            ) : (
              <div>Select invoice date to see budget impact.</div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!canSubmit || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ExpenseCreatePage;
